/**
 * WebRTC Factory
 * Factory pattern for creating WebRTC-related objects with dependency injection
 */

import { WebRTCConnection } from './WebRTCConnection';
import { AudioManager } from './AudioManager';
import { ConnectionStateMachine } from './ConnectionStateMachine';
import type { 
  WebRTCConnectionConfig, 
  WebRTCProviderInterface,
  InternalWebRTCConfig as WebRTCConfig
} from './WebRTCProvider';

/**
 * WebRTC dependency interface for testing and flexibility
 */
export interface WebRTCDependencies {
  mediaDevices?: MediaDevices;
  RTCPeerConnection?: typeof RTCPeerConnection;
  webrtcSDK?: any; // KVSWebRTC SDK
  console?: Console;
}

/**
 * Default browser dependencies
 */
const defaultDependencies: WebRTCDependencies = {
  mediaDevices: navigator.mediaDevices,
  RTCPeerConnection: RTCPeerConnection,
  webrtcSDK: null, // Will be set by WebRTCProvider after SDK loading
  console: console
};

/**
 * WebRTC Factory class
 * Creates WebRTC objects with proper dependency injection
 */
export class WebRTCFactory {
  private dependencies: WebRTCDependencies;

  constructor(dependencies: WebRTCDependencies = {}) {
    this.dependencies = { ...defaultDependencies, ...dependencies };
  }

  /**
   * Create WebRTC Connection with injected dependencies
   */
  createConnection(config: WebRTCConnectionConfig): WebRTCConnection {

    // Create audio manager
    const audioManager = this.createAudioManager();

    // Create state machine
    const stateMachine = this.createStateMachine();


    // Create connection with dependencies
    return new WebRTCConnection(config, {
      audioManager,
      stateMachine,
      dependencies: this.dependencies
    });
  }

  /**
   * Create Audio Manager
   */
  createAudioManager(): AudioManager {
    return new AudioManager({
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    });
  }

  /**
   * Create Connection State Machine
   */
  createStateMachine(): ConnectionStateMachine {
    return new ConnectionStateMachine('idle');
  }

  /**
   * Create RTCPeerConnection with ICE servers
   */
  createPeerConnection(iceServers: any[]): RTCPeerConnection {
    if (!this.dependencies.RTCPeerConnection) {
      throw new Error('RTCPeerConnection not available');
    }

    return new this.dependencies.RTCPeerConnection({
      iceServers: iceServers.map(server => ({
        urls: server.urls,
        username: server.username,
        credential: server.credential
      }))
    });
  }

  /**
   * Create SignalingClient with custom signer
   */
  createSignalingClient(config: {
    url: string;
    region: string;
    channelARN: string;
    clientId: string;
    channelEndpoint: string;
  }): any {
    if (!this.dependencies.webrtcSDK) {
      throw new Error('WebRTC SDK not available');
    }

    const customSigner = {
      getSignedURL: () => config.url
    };

    return new this.dependencies.webrtcSDK.SignalingClient({
      requestSigner: customSigner,
      region: config.region,
      role: this.dependencies.webrtcSDK.Role.VIEWER,
      clientId: config.clientId,
      channelARN: config.channelARN,
      channelEndpoint: config.channelEndpoint
    });
  }

  /**
   * Validate required dependencies
   */
  private validateDependencies(): void {
    const required = [
      'mediaDevices',
      'RTCPeerConnection'
    ] as const;

    for (const dep of required) {
      if (!this.dependencies[dep]) {
        throw new Error(`Required dependency missing: ${dep}`);
      }
    }

    if (!this.dependencies.webrtcSDK) {
      console.warn('⚠️ [WebRTCFactory] WebRTC SDK not available - some features may not work');
    }
  }

  /**
   * Update dependencies (useful for testing)
   */
  updateDependencies(newDependencies: Partial<WebRTCDependencies>): void {
    this.dependencies = { ...this.dependencies, ...newDependencies };
  }
}